import debug from 'debug';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import io from 'socket.io-client';
import { ToastType } from '@/components/Toast/Toast';
import { getConfig } from '@/config/config';
import { useController } from '@/hooks/useController';
import { useSessionId } from '@/hooks/useSessionId';
import { hostSelector, integrationIdSelector, avatarSelector, widgetIdSelector, clientContextSelector, identityTokenSelector, userConfigState, temporalToastSelector, initialLanguageSelector, versionSelector, } from '@/state/config';
import { chatEventsState, composeState, sessionState, userSelector, chatEventCountSelector, sessionIdSelector, leadersState, chatModalState, isLeaderSelector, sessionIdFromStorageState, isVisitorPresentSelector, filesToUploadState, sessionOverviewState, isWidgetOpenState, brainEventsQueue, nextQueuedEventSelector, showCompactResolved, lastBrainResponsesState, afterSurveyAction, } from '@/state/session';
import { connectionState, isOnlineState, shouldReconnectSelector, socketIdState, webChatStateState, } from '@/state/socket';
import { SocketEvent, } from '@/ts/socket';
import { parseHistory } from '@/util/history';
import { getTimestamp, isTest } from '@/util/utils';
import { getWebUser } from '@/util/web-user';
import { useEventListener } from './useEventListener';
import { useFileUploadHandlers } from './useFileUploadHandlers';
import { useSocket } from './useSocket';
import { useUnreadMessagesNotify } from './useUnreadMessagesNotify';
import { useWidgetState } from './useWidgetState';
// Errors that the system can't recover from, if one of them is received
// the error modal is displayed and the user will need to recreate the session
const UNRECOVERABLE_ERROR_EVENTS = [
    SocketEvent.app_error,
    SocketEvent.app_error_invalid_payload,
    SocketEvent.session_create_error,
    SocketEvent.session_reconnect_forbidden,
    SocketEvent.session_reconnect_error,
    SocketEvent.session_history_error,
    SocketEvent.session_max_concurrent,
];
export const useSocketSetup = () => {
    const { t, i18n } = useTranslation();
    const mounted = useRef(false);
    const isConnecting = useRef(false);
    const { closeWidget } = useWidgetState();
    const { notifySessionCreated, notifySessionReconnected, notifySessionClosed, notifyConversationClosed, } = useController();
    const { handleFileUpload } = useFileUploadHandlers();
    const integrationId = useRecoilValue(integrationIdSelector);
    const isVisitorPresent = useRecoilValue(isVisitorPresentSelector);
    const setSessionId = useSessionId(integrationId, true);
    const isEventQueueEmpty = !useRecoilValue(nextQueuedEventSelector);
    const [queueCloseConversation, setQueueCloseConversation] = useState();
    const [socket, setSocket] = useState();
    const [retryCount, setRetryCount] = useState(0);
    useUnreadMessagesNotify();
    const [socketId, setSocketIdState] = useRecoilState(socketIdState);
    const [webChatState, setWebChatState] = useRecoilState(webChatStateState);
    const [chatModal, setChatModal] = useRecoilState(chatModalState);
    const [connectionStatus, setConnectionState] = useRecoilState(connectionState);
    const shouldReconnect = useRecoilValue(shouldReconnectSelector);
    const avatar = useRecoilValue(avatarSelector);
    const chatCount = useRecoilValue(chatEventCountSelector);
    const clientContext = useRecoilValue(clientContextSelector);
    const connectedSessionId = useRecoilValue(sessionIdSelector);
    const sessionId = useRecoilValue(sessionIdFromStorageState);
    const host = useRecoilValue(hostSelector);
    const identityToken = useRecoilValue(identityTokenSelector);
    const isOpen = useRecoilValue(isWidgetOpenState);
    const user = useRecoilValue(userSelector);
    const userState = useRecoilValue(userConfigState);
    const widgetId = useRecoilValue(widgetIdSelector);
    const isLeader = useRecoilValue(isLeaderSelector);
    const setSessionOverview = useSetRecoilState(sessionOverviewState);
    const filesToUpload = useRecoilValue(filesToUploadState);
    const version = useRecoilValue(versionSelector);
    const compactResolved = useRecoilValue(showCompactResolved);
    const [afterSurveyActionState, setAfterSurveyActionState] = useRecoilState(afterSurveyAction);
    const setIsOnline = useSetRecoilState(isOnlineState);
    const setChatEvents = useSetRecoilState(chatEventsState);
    const setComposeState = useSetRecoilState(composeState);
    const setLeader = useSetRecoilState(leadersState);
    const setSessionState = useSetRecoilState(sessionState);
    const setFilesToUpload = useSetRecoilState(filesToUploadState);
    const setToast = useSetRecoilState(temporalToastSelector);
    const addBrainEventToQueue = useSetRecoilState(brainEventsQueue);
    const setLastBrainResponses = useSetRecoilState(lastBrainResponsesState);
    const { updateLocation, closeSession } = useSocket(socket);
    const initialLanguage = useRecoilValue(initialLanguageSelector);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:socket`), [widgetId]);
    // Connect to the server and save the reference to the socket in the context
    useEffect(() => {
        if (integrationId && host && !socket) {
            const client = io(host, {
                // We always send the `upgrade` header to channels so we don't need to tell engine.io to upgrade
                upgrade: false,
                rememberUpgrade: true,
                autoConnect: false,
                path: `/v1/web/${integrationId}/ws`,
                transports: ['websocket'],
                reconnection: false,
            });
            queueMicrotask(() => setSocket(client));
        }
    }, [host, integrationId, socket]);
    useEffect(() => {
        mounted.current = true;
        if (socket) {
            socket.on('error', (error) => {
                log('socket.on(error): ', error);
            });
            socket.on('connect_error', (error) => {
                log('socket.connect_error', error);
                setConnectionState('offline');
                isConnecting.current = false;
            });
            socket.on('disconnect', (reason) => {
                if (!mounted.current) {
                    // We can't update the recoil state if the component is unmounted
                    return;
                }
                log('socket.disconnect reason:', reason);
                setConnectionState('offline');
                setSocketIdState(undefined);
                setRetryCount(0);
                isConnecting.current = false;
            });
            socket.on('connect', () => {
                log('socket.connect', `${socket.id} at ${getTimestamp()}`);
                setSocketIdState(socket.id);
                setConnectionState('online');
                setWebChatState("idle" /* WebChatState.Idle */);
                isConnecting.current = false;
            });
            // Session reconnected
            socket.on(SocketEvent.session_reconnected, (data) => {
                var _a, _b, _c, _d;
                setWebChatState("synchronize" /* WebChatState.Synchronize */);
                setSessionState(data);
                setChatModal(null);
                notifySessionReconnected({
                    user_id: (_b = (_a = data.context) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.user_id,
                    session_id: data.session_id,
                    display_name: (_d = (_c = data.context) === null || _c === void 0 ? void 0 : _c.user) === null || _d === void 0 ? void 0 : _d.display_name,
                });
            });
            // Context updated
            socket.on(SocketEvent.context_updated, (data) => {
                setSessionState((prev) => Object.assign({}, prev, { context: data.context }));
            });
            // Messages from agents
            socket.on(SocketEvent.message_received, (event) => {
                // Mark message as delivered
                socket.volatile.emit(SocketEvent.message_delivered, {
                    session_id: event.session_id,
                    timestamp: getTimestamp(),
                });
                // Transform the Agent message event from the socket to a chat event
                const chatEvent = {
                    event_id: nanoid(),
                    event_type: 'agent_message',
                    author_name: event.from.agent_name,
                    author_avatar: event.from.agent_avatar,
                    author_id: event.from.agent_id,
                    body: event.body,
                    text: event.text,
                };
                setChatEvents((currentState) => [...currentState, chatEvent]);
            });
            // Messages from brains.
            socket.on(SocketEvent.message_brain_received, async (event) => {
                // Mark message as delivered
                socket.volatile.emit(SocketEvent.message_delivered, {
                    session_id: event.session_id,
                    timestamp: getTimestamp(),
                });
                // Set the context from the brain response to validate if we have
                // or not fields. The context is masked.
                setSessionState((prev) => Object.assign({}, prev, { context: event.context }));
                if (event.brain_language && !initialLanguage) {
                    i18n.changeLanguage(event.brain_language);
                }
                const responses = event.output.responses;
                const rootEvent = {
                    event_type: 'brain_message',
                    author_name: 'common.ai_agent',
                    author_avatar: avatar,
                    author_id: nanoid(),
                    created_at: getTimestamp(),
                    source: 'live',
                };
                const brainResponsesEvents = responses.map((resp) => {
                    var _a, _b;
                    // Transform the brain message event from the socket to a chat event
                    const chatEvent = Object.assign(Object.assign({}, rootEvent), { event_id: nanoid(), response: resp, request_id: event.request_id, collection: (_b = (_a = event.output) === null || _a === void 0 ? void 0 : _a.debug) === null || _b === void 0 ? void 0 : _b.collection });
                    return chatEvent;
                });
                // Store all the responses from the latest brain message
                setLastBrainResponses(brainResponsesEvents);
                // Add event responses to the messages queue
                addBrainEventToQueue((currentState) => [
                    ...currentState,
                    ...brainResponsesEvents,
                ]);
            });
            // Member join event
            socket.on(SocketEvent.member_join, (event) => {
                const chatEvent = {
                    event_id: nanoid(),
                    event_type: 'member_join',
                    author_name: event.author_name ||
                        userState.virtual_agent_name ||
                        'common.ai_agent',
                    author_type: event.author_type,
                    author_avatar: getConfig().BRAIN_AVATAR,
                    author_id: event.author_id,
                };
                addBrainEventToQueue((prev) => [...prev, chatEvent]);
            });
            // Member leave event
            socket.on(SocketEvent.member_leave, (event) => {
                const chatEvent = {
                    event_id: nanoid(),
                    event_type: 'member_leave',
                    author_name: event.author_name ||
                        userState.virtual_agent_name ||
                        'common.ai_agent',
                    author_type: event.author_type,
                    author_avatar: getConfig().BRAIN_AVATAR,
                    author_id: event.author_id,
                };
                addBrainEventToQueue((prev) => [...prev, chatEvent]);
            });
            // Message delivered
            socket.on(SocketEvent.message_delivered, (event) => {
                setChatEvents((currentState) => currentState.map((chatEvent) => {
                    if (chatEvent.event_type === 'user_message') {
                        const uMe = chatEvent;
                        if (!uMe.delivered_at && uMe.created_at < event.timestamp) {
                            return Object.assign(Object.assign({}, uMe), { delivered_at: event.timestamp });
                        }
                    }
                    return chatEvent;
                }));
            });
            // Message read
            socket.on(SocketEvent.message_read, (event) => {
                setChatEvents((currentState) => currentState.map((chatEvent) => {
                    if (chatEvent.event_type === 'user_message') {
                        const uMe = chatEvent;
                        if (uMe.read_at !== null && uMe.created_at < event.timestamp) {
                            return Object.assign(Object.assign({}, uMe), { read_at: event.timestamp });
                        }
                    }
                    return chatEvent;
                }));
            });
            // Compose event
            socket.on(SocketEvent.message_compose, (event) => {
                // We ignore compose messages from the visitor
                // * For the visitor we keep track of compose in a boolean in MessageInput
                if (event.author_type === 'visitor') {
                    return;
                }
                // * For the brain we stop typing when we received the messages instead of when we get the event
                if (event.author_type === 'brain' && event.action === 'stop') {
                    return;
                }
                const compose = {
                    event_id: nanoid(),
                    event_type: 'compose',
                    author_name: event.author_name,
                    author_type: event.author_type,
                    author_avatar: getConfig().BRAIN_AVATAR,
                    author_id: event.author_id,
                    action: event.action,
                };
                setComposeState(compose);
            });
            socket.onAny(log.extend(':incoming'));
            // Only connect if not already connecting or connected
            if (!socket.connected && !isConnecting.current) {
                isConnecting.current = true;
                socket.connect();
            }
        }
        return () => {
            mounted.current = false;
            socket === null || socket === void 0 ? void 0 : socket.close();
        };
    }, [
        log,
        socket,
        avatar,
        setChatEvents,
        setChatModal,
        setComposeState,
        setConnectionState,
        setSessionState,
        setSocketIdState,
        setWebChatState,
        addBrainEventToQueue,
        notifySessionReconnected,
        userState.virtual_agent_name,
        i18n,
        initialLanguage,
        setLastBrainResponses,
    ]);
    // Error handler that updates the session with the error data
    useEffect(() => {
        if (socket) {
            const handleError = (event) => (sError) => {
                setWebChatState("error" /* WebChatState.Error */);
                const message_key = event === SocketEvent.session_max_concurrent
                    ? 'errors.max_concurrent'
                    : 'errors.unknown';
                const error = Object.assign(Object.assign({}, sError === null || sError === void 0 ? void 0 : sError.error), { event, message_key });
                setSessionState((prev) => Object.assign({}, prev, { error }));
            };
            UNRECOVERABLE_ERROR_EVENTS.forEach((event) => {
                socket.on(event, handleError(event));
            });
        }
    }, [setSessionState, setWebChatState, socket]);
    // Leader event
    useEffect(() => {
        const handleLeaderEvent = (data) => {
            setLeader((prev) => Object.assign({}, prev, { [data.session_id]: data.leader_id }));
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_leader, handleLeaderEvent);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_leader, handleLeaderEvent);
        };
    }, [setLeader, socket]);
    // Session rated event
    useEffect(() => {
        const handleSessionRatedEvent = () => {
            // Execute the stored action after receiving the rated event
            if (afterSurveyActionState) {
                afterSurveyActionState();
                // Clear the action after execution
                setAfterSurveyActionState(null);
            }
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_rated, handleSessionRatedEvent);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_rated, handleSessionRatedEvent);
        };
    }, [afterSurveyActionState, setAfterSurveyActionState, socket]);
    // Session created
    // Set session state, chat events, webchatState and sessionId in localStorage
    useEffect(() => {
        const handleSessionCreated = (data) => {
            var _a, _b, _c, _d;
            // Ignore the new session if we were not creating one
            // This could happen if while creating the session, this client was
            // instructed to reconnect to another session.
            if (webChatState !== "creating" /* WebChatState.Creating */) {
                log(`Session created while creating another one`, data);
            }
            notifySessionCreated({
                user_id: (_b = (_a = data.context) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.user_id,
                session_id: data.session_id,
                display_name: (_d = (_c = data.context) === null || _c === void 0 ? void 0 : _c.user) === null || _d === void 0 ? void 0 : _d.display_name,
            });
            setSessionState(Object.assign(Object.assign({}, data), { status: "open" /* SessionStatus.Open */ }));
            setChatModal(null);
            setChatEvents([]);
            setSessionOverview(null);
            setSessionId(data.session_id);
            // Delays execution of events until the code in notifySessionCreated has been executed.
            setTimeout(() => {
                setWebChatState("synchronized" /* WebChatState.Synchronized */);
            }, 50);
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_created, handleSessionCreated);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_created, handleSessionCreated);
        };
    }, [
        log,
        notifySessionCreated,
        setChatEvents,
        setChatModal,
        setSessionId,
        setSessionOverview,
        setSessionState,
        setWebChatState,
        socket,
        webChatState,
    ]);
    // Conversation closed event
    // Update session state with `status: 'closed'`
    useEffect(() => {
        const handleConversationClosed = (data) => {
            setQueueCloseConversation(data);
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.conversation_closed, handleConversationClosed);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.conversation_closed, handleConversationClosed);
        };
    }, [notifyConversationClosed, socket]);
    // Session expired event
    // Update session state with `status: expired`, if no events, create session
    useEffect(() => {
        const handleSessionExpired = (data) => {
            setSessionState((prev) => Object.assign({}, prev, data, {
                status: "expired" /* SessionStatus.Expired */,
                remaining_keep_alive: undefined,
            }));
            if (data.leader_id) {
                setLeader((prev) => Object.assign({}, prev, { [data.session_id]: data.leader_id }));
            }
            if (webChatState === "reconnecting" /* WebChatState.Reconnecting */) {
                setWebChatState("synchronized" /* WebChatState.Synchronized */);
            }
            // Show the expired modal so the user can submit a rating if he wants
            if (chatCount > 0) {
                setChatModal("expired" /* ChatModal.Expired */);
                return;
            }
            if (isVisitorPresent) {
                // The timeout is necessary to ensure that Recoil has updated
                // the state and leader selector correctly.
                setTimeout(() => {
                    setSessionId(null);
                    setWebChatState("idle" /* WebChatState.Idle */);
                }, 0);
            }
            else {
                log('Disconnect socket due to expired session and visitor absence.');
                socket === null || socket === void 0 ? void 0 : socket.disconnect();
            }
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_expired, handleSessionExpired);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_expired, handleSessionExpired);
        };
    }, [
        log,
        socket,
        isLeader,
        socketId,
        chatCount,
        isVisitorPresent,
        setSessionState,
        setSessionId,
        setChatModal,
        setLeader,
        setWebChatState,
        webChatState,
        userState,
        closeSession,
    ]);
    // Conversation reopened event
    // Update session state with `status: 'closed'`
    useEffect(() => {
        const handleConversationReopened = () => {
            setSessionState((prev) => Object.assign({}, prev, {
                status: "open" /* SessionStatus.Open */,
                remaining_keep_alive: undefined,
            }));
            if (chatModal === "closed" /* ChatModal.Resolved */) {
                setChatModal(null);
            }
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.conversation_reopened, handleConversationReopened);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.conversation_reopened, handleConversationReopened);
        };
    }, [socket, chatModal, setSessionState, setChatModal]);
    // Session closed / close_error / rating_error event
    // Close the widget and remove the existing session id from localstorage
    useEffect(() => {
        const handleSessionClosed = () => {
            closeWidget();
            setChatEvents([]);
            setSessionOverview(null);
            setChatModal(null);
            setAfterSurveyActionState(null);
            notifySessionClosed();
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_closed, handleSessionClosed);
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_close_error, handleSessionClosed);
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_rating_error, handleSessionClosed);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_closed, handleSessionClosed);
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_close_error, handleSessionClosed);
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_rating_error, handleSessionClosed);
        };
    }, [
        socket,
        setSessionId,
        closeWidget,
        setChatModal,
        setChatEvents,
        notifySessionClosed,
        setSessionOverview,
        setAfterSurveyActionState,
    ]);
    // Message send sync event
    useEffect(() => {
        const handleMessageSendSync = (data) => {
            const chatEvent = Object.assign(Object.assign({ event_id: nanoid(), event_type: 'user_message', author_name: (user === null || user === void 0 ? void 0 : user.display_name) || '', author_avatar: user === null || user === void 0 ? void 0 : user.avatar, author_id: (user === null || user === void 0 ? void 0 : user.user_id) || '', created_at: data.timestamp }, (data.input.text && { text: data.input.text })), (data.input.attachments && { attachments: data.input.attachments }));
            setChatEvents((currentState) => [...currentState, chatEvent]);
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.message_send_sync, handleMessageSendSync);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.message_send_sync, handleMessageSendSync);
        };
    }, [user, socket, setChatEvents]);
    // Session History Sync
    useEffect(() => {
        const handleHistorySync = (data) => {
            const { context, events, status, remaining_keep_alive } = parseHistory(data, avatar, t, user);
            if (events) {
                setChatEvents(events);
            }
            if (context) {
                setSessionState((prev) => Object.assign({}, prev, { context: data.context }));
            }
            // if keep_alive is set to 0, the session is open
            // if keep_alive is set to > 0, the session is closed
            setSessionState((prev) => Object.assign({}, prev, {
                remaining_keep_alive,
                status,
            }));
            // TODO: Remove version check once we have deprecated the old version
            if (status === "closed" /* SessionStatus.Resolved */ &&
                (version !== 'v2' || !compactResolved)) {
                setChatModal("closed" /* ChatModal.Resolved */);
            }
            setWebChatState("synchronized" /* WebChatState.Synchronized */);
            socket === null || socket === void 0 ? void 0 : socket.emit(SocketEvent.context_update, {
                session_id: data.session_id,
                context: {
                    global: Object.assign({}, clientContext),
                },
                timestamp: getTimestamp(),
            });
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_history_synced, handleHistorySync);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_history_synced, handleHistorySync);
        };
    }, [
        user,
        socket,
        avatar,
        setChatModal,
        setChatEvents,
        setSessionState,
        setWebChatState,
        t,
        clientContext,
        version,
        compactResolved,
    ]);
    useEffect(() => {
        if (isOpen && webChatState === "idle" /* WebChatState.Idle */) {
            setWebChatState("init" /* WebChatState.Init */);
        }
    }, [isOpen, webChatState, setWebChatState]);
    // Use effect that controls the next state the app needs to be. If a session
    // needs to be created, reconnected, synchronized, etc..
    useEffect(() => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) ||
            connectionStatus !== 'online' ||
            ![
                "idle" /* WebChatState.Idle */,
                "create" /* WebChatState.Create */,
                "reconnect" /* WebChatState.Reconnect */,
                "synchronize" /* WebChatState.Synchronize */,
                "synchronized" /* WebChatState.Synchronized */,
                "init" /* WebChatState.Init */,
            ].includes(webChatState)) {
            return;
        }
        log(`[state-machine] sessionId:${sessionId}, webChatState:${webChatState}`);
        switch (webChatState) {
            // Create a new session
            case "create" /* WebChatState.Create */:
                setWebChatState("creating" /* WebChatState.Creating */);
                socket.emit(SocketEvent.session_create, {
                    context: {
                        global: Object.assign({}, clientContext),
                        user: getWebUser(),
                    },
                    identity_token: identityToken || undefined,
                    timestamp: getTimestamp(),
                    is_test: isTest(userState.clientUrl),
                });
                break;
            // Reconnect session
            case "reconnect" /* WebChatState.Reconnect */:
                if (sessionId) {
                    setWebChatState("reconnecting" /* WebChatState.Reconnecting */);
                    socket.emit(SocketEvent.session_reconnect, {
                        session_id: sessionId,
                        timestamp: getTimestamp(),
                    });
                }
                else {
                    log('WARN: Reconnect without sessionId');
                }
                break;
            // Synchronize session
            case "synchronize" /* WebChatState.Synchronize */:
                if (sessionId) {
                    setWebChatState("synchronizing" /* WebChatState.Synchronizing */);
                    socket.emit(SocketEvent.session_history_sync, {
                        session_id: sessionId,
                        timestamp: getTimestamp(),
                    });
                }
                break;
            // Initiate a new conversation, creating it or reconnecting to an existing one
            case "init" /* WebChatState.Init */:
                setWebChatState(sessionId ? "reconnect" /* WebChatState.Reconnect */ : "create" /* WebChatState.Create */);
                break;
            // Reconnect to a newly session
            case "synchronized" /* WebChatState.Synchronized */:
                if (connectedSessionId &&
                    sessionId &&
                    connectedSessionId !== sessionId) {
                    log(`Connect to different session: ${sessionId}`);
                    setWebChatState("reconnect" /* WebChatState.Reconnect */);
                }
                else {
                    updateLocation();
                }
                break;
            default: {
                log(`WARN: ignore webChatState: ${webChatState}`);
            }
        }
    }, [
        log,
        socket,
        userState,
        sessionId,
        connectedSessionId,
        webChatState,
        clientContext,
        identityToken,
        connectionStatus,
        setWebChatState,
        updateLocation,
    ]);
    // Listen to the window online event and reconnect the session if the user
    // is back online. This is useful when the user is on a mobile device and
    // the connection is lost. The user can still type messages and when the
    // connection is back, the session will be reconnected.
    // Check the `shouldReconnect` selector for more information.
    const handleNetworkStatusChange = useCallback(() => {
        if (window.navigator.onLine) {
            setIsOnline(true);
        }
        else {
            setIsOnline(false);
        }
    }, [setIsOnline]);
    useEventListener('online', handleNetworkStatusChange);
    useEventListener('offline', handleNetworkStatusChange);
    // Show the conversation close only when the brain finished showing the
    // responses
    useEffect(() => {
        if (queueCloseConversation && isEventQueueEmpty) {
            const { keep_alive, timestamp } = queueCloseConversation;
            const remaining_keep_alive = -1 * ((getTimestamp() - timestamp) / 1000 - keep_alive);
            // Defer state updates to avoid synchronous setState in effect
            queueMicrotask(() => {
                // remove the queue to close the conversation and set the remaining
                // keep alive time
                setQueueCloseConversation(undefined);
                setSessionState((prev) => Object.assign({}, prev, {
                    status: "closed" /* SessionStatus.Resolved */,
                    remaining_keep_alive,
                }));
                if (chatModal !== "survey" /* ChatModal.Survey */ &&
                    chatModal !== "survey-full" /* ChatModal.SurveyFull */) {
                    // Less than 5 seconds left is not enough to show the countdown.
                    // We show the expired modal instead
                    // TODO: Remove version check once we have deprecated the old version
                    if (remaining_keep_alive > 5 &&
                        (version !== 'v2' || !compactResolved)) {
                        setChatModal("closed" /* ChatModal.Resolved */);
                    }
                    else if (remaining_keep_alive > 0 && remaining_keep_alive < 5) {
                        setChatModal("expired" /* ChatModal.Expired */);
                    }
                }
                // Notify the conversation has been closed
                notifyConversationClosed();
            });
        }
    }, [
        chatModal,
        isEventQueueEmpty,
        queueCloseConversation,
        setChatModal,
        setSessionState,
        setQueueCloseConversation,
        notifyConversationClosed,
        version,
        compactResolved,
    ]);
    useEffect(() => {
        const handleUploadRequestSuccess = async (data) => {
            // find file to upload from filesToUpload
            const f = filesToUpload.find((file) => file.file_id === data.file_id);
            // upload file
            await handleFileUpload(f, data.upload_url);
            // update upload_url of file
            setFilesToUpload((prev) => {
                return prev.map((file) => {
                    if (file.file_id === data.file_id) {
                        return Object.assign(Object.assign({}, file), { upload_url: data.upload_url, filename: data.file_name });
                    }
                    return file;
                });
            });
        };
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_files_upload_request_success, handleUploadRequestSuccess);
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_files_upload_request_success, handleUploadRequestSuccess);
        };
    }, [filesToUpload, handleFileUpload, setFilesToUpload, socket]);
    // request file upload url error
    useEffect(() => {
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_files_upload_request_error, (data) => {
            if ('file_id' in data) {
                setFilesToUpload((prev) => {
                    return prev.filter((file) => file.file_id !== data.file_id);
                });
                setToast((prevToasts) => [
                    ...prevToasts,
                    {
                        type: ToastType.attention,
                        message: `Error with file: ${data.file_name}, please try again.`,
                    },
                ]);
            }
            else {
                setFilesToUpload([]);
                setToast((prevToasts) => [
                    ...prevToasts,
                    {
                        type: ToastType.attention,
                        message: data.message,
                    },
                ]);
            }
        });
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_files_upload_request_error);
        };
    }, [setFilesToUpload, setToast, socket]);
    // request file download url success
    useEffect(() => {
        socket === null || socket === void 0 ? void 0 : socket.on(SocketEvent.session_files_download_request_success, (data) => {
            setChatEvents((prev) => {
                const updatedEvents = [...prev];
                for (let i = 0; i < updatedEvents.length; i++) {
                    const event = updatedEvents[i];
                    if (event.event_type === 'user_message') {
                        const uMe = event;
                        if (uMe.attachments) {
                            const attachmentIndex = uMe.attachments.findIndex((attachment) => attachment.id === data.file_id);
                            if (attachmentIndex !== -1) {
                                const updatedAttachments = [...uMe.attachments];
                                updatedAttachments[attachmentIndex] = Object.assign(Object.assign({}, updatedAttachments[attachmentIndex]), { url: data.download_url });
                                updatedEvents[i] = Object.assign(Object.assign({}, uMe), { attachments: updatedAttachments });
                                break;
                            }
                        }
                    }
                }
                return updatedEvents;
            });
        });
        return () => {
            socket === null || socket === void 0 ? void 0 : socket.off(SocketEvent.session_files_download_request_success);
        };
    }, [setChatEvents, socket]);
    useEffect(() => {
        if (shouldReconnect) {
            // exponential backoff with randomization
            const randomFactor = 0.5 + Math.random();
            const delay = 1000 * Math.pow(2, retryCount) * randomFactor;
            const timeoutId = setTimeout(() => {
                // Always increment retry count to ensure future attempts are scheduled
                setRetryCount((prev) => prev + 1);
                // Only connect if not already connecting or connected
                if (socket && !socket.connected && !isConnecting.current) {
                    setConnectionState('connecting');
                    isConnecting.current = true;
                    socket.connect();
                }
            }, delay);
            return () => clearTimeout(timeoutId);
        }
    }, [shouldReconnect, retryCount, socket, setConnectionState, log]);
    return socket;
};
